import React from 'react'
import { all_routes } from '../../router/all_routes'
import { Link, useParams } from 'react-router-dom'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import Table from '../../../core/common/dataTable/index'
import InstructorSidebar from '../common/instructorSidebar'
import { useQuery } from '@tanstack/react-query'
import {
  getExtendedUserDetail,
  ExtendedUserDetailResponse,
} from '../../../api/user'
import dayjs from 'dayjs'
import { Flex, Spin } from 'antd'

const UserDetails = () => {
  const { id } = useParams<{ id: string }>()
  const responseQuery = useQuery<ExtendedUserDetailResponse>({
    queryKey: ['admin-users-extended', id],
    queryFn: () => getExtendedUserDetail(id || '', true),
    enabled: !!id,
  })
  const userDetail = responseQuery?.data?.data?.user
  const transactions = responseQuery?.data?.data?.transactions || []
  const transactionColumns = [
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      render: (text: string) => <Link to="#">{text}</Link>,
      sorter: (a: any, b: any) => a.order_id.length - b.order_id.length,
    },
    {
      title: 'Course',
      dataIndex: 'course',
      render: (course: any) => course?.title || '-',
      sorter: (a: any, b: any) =>
        (a.course?.title || '').length - (b.course?.title || '').length,
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      render: (text: string) => dayjs(text).format('DD MMM YYYY'),
      sorter: (a: any, b: any) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: 'Amount',
      dataIndex: 'total',
      render: (text: string) =>
        `Rp ${parseFloat(text).toLocaleString('id-ID')}`,
      sorter: (a: any, b: any) => parseFloat(a.total) - parseFloat(b.total),
    },
    {
      title: 'Payment Method',
      dataIndex: 'type',
      render: (text: string) => text?.toUpperCase() || '-',
      sorter: (a: any, b: any) => (a.type || '').length - (b.type || '').length,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text: string) => (
        <span
          className={`badge badge-sm ${
            text === 'paid'
              ? 'bg-success'
              : text === 'pending'
              ? 'bg-warning'
              : text === 'failed'
              ? 'bg-danger'
              : 'bg-secondary'
          } d-inline-flex align-items-center`}
        >
          <i className="fa-solid fa-circle fs-5 me-1" />
          {text?.toUpperCase() || '-'}
        </span>
      ),
      sorter: (a: any, b: any) =>
        (a.status || '').length - (b.status || '').length,
    },
  ]

  return (
    <>
      <div className="content instructor-detail-content">
        <div className="container">
          {/* <ProfileCard /> */}

          <div className="row">
            <InstructorSidebar />
            <div className="col-lg-9">
              <Link
                to={all_routes.adminUser}
                className="d-flex align-items-center mb-3"
              >
                <i className="isax isax-arrow-left me-1 fw-bold" />
                Back to List
              </Link>
              {responseQuery.isLoading ? (
                <Flex className="h-100" align="center" justify="center">
                  <Spin tip="Loading..." size="large" />
                </Flex>
              ) : (
                <>
                  <div className="row">
                    <div className="col-lg-8">
                      <div className="instructor-details-item1 mb-4">
                        <div
                          className="instructor-details"
                          style={{ gap: '24px' }}
                        >
                          <div className="">
                            <Link to="#">
                              <ImageWithBasePath
                                src={'/assets/img/students/student-01.jpg'}
                                srcApi={userDetail?.profile_picture}
                                alt="img"
                                className="img-fluid rounded-circle"
                                style={{
                                  width: '120px',
                                  height: '120px',
                                  objectFit: 'cover',
                                  aspectRatio: '1 / 1',
                                }}
                                width={'max-content'}
                                isSquare={false}
                                forceWidth={'max-content'}
                              />
                            </Link>
                          </div>
                          <div className="flex-fill">
                            <div className="pb-3 mb-3">
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <h6 className="fs-18 fw-bold">
                                  <Link to="#">{userDetail?.fullname}</Link>
                                </h6>
                              </div>
                              <div className="d-flex align-items-center mb-1">
                                <p>
                                  NIK :{' '}
                                  {userDetail?.nik
                                    ? userDetail?.nik
                                    : '-'}
                                </p>
                              </div>
                              <div className="d-flex align-items-center mb-1">
                                <p>
                                  Joined on :{' '}
                                  {userDetail?.created_at
                                    ? dayjs(userDetail?.created_at).format(
                                        'DD MMMM YYYY',
                                      )
                                    : '-'}
                                </p>
                              </div>
                              <div className="mt-3">
                                <p>{userDetail?.bio || '-'}</p>
                              </div>
                              {userDetail?.profession && (
                                <div className="mt-2">
                                  <p>
                                    <strong>Profession:</strong>{' '}
                                    {userDetail.profession}
                                  </p>
                                </div>
                              )}
                              {userDetail?.agency && (
                                <div className="mt-2">
                                  <p>
                                    <strong>Agency:</strong> {userDetail.agency}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="contact-card border-0 mb-0">
                        <div className="contact-details-body">
                          <h5 className="mb-3 fw-bold">Contact Details</h5>
                          <div className="d-flex align-items-center mb-4">
                            <span className="contact-icon flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center me-3">
                              <i className="fa-regular fa-envelope" />
                            </span>
                            <div>
                              <h6 className="mb-0">Email</h6>
                              <p className="fs-14 mb-0">{userDetail?.email}</p>
                            </div>
                          </div>
                          <div className="d-flex align-items-center mb-4">
                            <span className="contact-icon flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center me-3">
                              <i className="isax isax-call" />
                            </span>
                            <div>
                              <h6 className="mb-0">Phone</h6>
                              <p className="fs-14 mb-0">
                                {userDetail?.phone_number}
                              </p>
                            </div>
                          </div>
                          {userDetail?.gender && (
                            <div className="d-flex align-items-center mb-4">
                              <span className="contact-icon flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center me-3">
                                <i className="fa-solid fa-user" />
                              </span>
                              <div>
                                <h6 className="mb-0">Gender</h6>
                                <p className="fs-14 mb-0 text-capitalize">
                                  {userDetail.gender}
                                </p>
                              </div>
                            </div>
                          )}
                          {userDetail?.dob && (
                            <div className="d-flex align-items-center mb-4">
                              <span className="contact-icon flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center me-3">
                                <i className="fa-solid fa-calendar" />
                              </span>
                              <div>
                                <h6 className="mb-0">Date of Birth</h6>
                                <p className="fs-14 mb-0">
                                  {dayjs(userDetail.dob).format('DD MMMM YYYY')}
                                </p>
                              </div>
                            </div>
                          )}
                          {userDetail?.address && (
                            <div className="d-flex align-items-center">
                              <span className="contact-icon flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center me-3">
                                <i className="isax isax-location" />
                              </span>
                              <div>
                                <h6 className="mb-0">Address</h6>
                                <p className="fs-14 mb-0 text-truncate text-wrap">
                                  {userDetail.address}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="statements my-5">
                    <h5 className="page-title">Transaction History</h5>
                    <div className="table-top">
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <div className="d-flex align-items-center">
                            <div className="mb-3"></div>
                          </div>
                        </div>
                        <div className="col-md-4"></div>
                      </div>
                    </div>
                    <Table
                      dataSource={transactions}
                      columns={transactionColumns}
                      Search={false}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default UserDetails
